// src/services/sessionsService.js
import apiClient from './api.js';

class SessionsService {
  // Get all class sessions with optional date filtering
  async getSessions(params = {}) {
    try {
      const response = await apiClient.get('/class-sessions/user', params);
      return response;
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  }

  // Get sessions for a specific date range (useful for schedule view)
  async getSessionsForDateRange(startDate, endDate) {
    try {
      const params = {
        startDate: startDate,
        endDate: endDate
      };
      const response = await apiClient.get('/class-sessions/user', params);
      
      // Transform the sessions from backend format to frontend format
      const sessions = response.sessions || response;
      
      if (Array.isArray(sessions)) {
        const transformedSessions = this.transformSessionsFromBackend(sessions);
        return transformedSessions;
      }
      
      return sessions;
    } catch (error) {
      console.error('Error fetching sessions for date range:', error);
      throw error;
    }
  }

  // Get sessions for a specific class
  async getSessionsForClass(classId) {
    try {
      const response = await apiClient.get(`/class-sessions?classId=${classId}`);
      return response;
    } catch (error) {
      console.error('Error fetching sessions for class:', error);
      throw error;
    }
  }

  // Create a new session
  async createSession(sessionData) {
    try {
      const response = await apiClient.post('/class-sessions', sessionData);
      return response;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  // Update session
  async updateSession(sessionId, updates) {
    try {
      const response = await apiClient.put(`/class-sessions/${sessionId}`, updates);
      return this.transformSessionFromBackend(response);
    } catch (error) {
      console.error('Error updating session:', error);
      throw error;
    }
  }

  // Update multiple sessions in a batch (for moving entire class series)
  async updateSessionsBatch(updates) {
    try {
      const response = await apiClient.put('/class-sessions/batch', { updates });
      return response.sessions.map(session => this.transformSessionFromBackend(session));
    } catch (error) {
      console.error('Error updating sessions in batch:', error);
      throw error;
    }
  }

  // Delete session
  async deleteSession(sessionId) {
    try {
      const response = await apiClient.delete(`/class-sessions/${sessionId}`);
      return response;
    } catch (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  }

  // Helper function to safely parse dates from backend
  parseBackendDate(dateString) {
    if (!dateString) return null;
    
    // If it's already a date-only string, return as is
    if (!dateString.includes('T')) return dateString;
    
    // If it's an ISO datetime, extract date components safely
    const utcDate = new Date(dateString);
    const year = utcDate.getUTCFullYear();
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getUTCDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

  // Transform backend session data to frontend format
  transformSessionFromBackend(backendSession) {
    const transformed = {
      id: backendSession.id,
      classId: backendSession.class_id || backendSession.classId,
      className: backendSession.class_title || backendSession.className,
      sessionDate: this.parseBackendDate(backendSession.session_date) || backendSession.sessionDate,
      startTime: backendSession.start_time || backendSession.startTime,
      endTime: backendSession.end_time || backendSession.endTime,
      duration: backendSession.duration,
      status: backendSession.status,
      notes: backendSession.notes,
      maxCapacity: backendSession.max_capacity || backendSession.maxCapacity,
      currentEnrollment: backendSession.current_enrollment || backendSession.currentEnrollment,
      createdAt: backendSession.created_at || backendSession.createdAt,
      updatedAt: backendSession.updated_at || backendSession.updatedAt,

      // Include block details if class is part of a block
      blockId: backendSession.block_id || backendSession.blockId,
      blockName: backendSession.block_name || backendSession.blockName,
      blockStartDate: backendSession.block_start_date || backendSession.blockStartDate,
      blockEndDate: backendSession.block_end_date || backendSession.blockEndDate,
      blockStatus: backendSession.block_status || backendSession.blockStatus,

      // Include class details if joined
      title: backendSession.class_title || backendSession.title,
      instructor: backendSession.instructor,
      category: backendSession.category,
      skillLevel: backendSession.skill_level || backendSession.skillLevel,
      location: backendSession.location,
      price: backendSession.price,
      description: backendSession.description,
      tags: backendSession.tags || []
    };

    return transformed;
  }

  // Transform multiple sessions from backend
  transformSessionsFromBackend(backendSessions) {
    return backendSessions.map(session => this.transformSessionFromBackend(session));
  }
}

// Create singleton instance
const sessionsService = new SessionsService();

export default sessionsService;